import { MARKETING_ATTRIBUTION_COOKIE_NAME } from "@shared/const";
import { hasMarketingConsent } from "@/components/CookieConsent";
import {
  hasMarketingSignal,
  MARKETING_CLICK_ID_PARAMS,
  MARKETING_QUERY_PARAMS,
  parseMarketingAttribution,
  stringifyMarketingAttribution,
  type MarketingAttribution,
  type MarketingClickIdParam,
  type MarketingQueryParam,
  type MarketingTouchpoint,
} from "@shared/marketingAttribution";

const ATTRIBUTION_STORAGE_KEY = "double-o-marketing-attribution";
const ATTRIBUTION_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

function truncate(value: string, maxLength: number): string {
  return value.length > maxLength ? value.slice(0, maxLength) : value;
}

function readStringParams<TParam extends string>(
  searchParams: URLSearchParams,
  paramNames: readonly TParam[],
): Partial<Record<TParam, string>> {
  const params: Partial<Record<TParam, string>> = {};

  for (const paramName of paramNames) {
    const value = searchParams.get(paramName);
    if (!value) continue;
    params[paramName] = truncate(value, 255);
  }

  return params;
}

function getExternalReferrer(): URL | null {
  if (typeof document === "undefined" || !document.referrer) return null;

  try {
    const referrerUrl = new URL(document.referrer);
    if (referrerUrl.host === window.location.host) return null;
    return referrerUrl;
  } catch {
    return null;
  }
}

function buildTouchpoint(): MarketingTouchpoint {
  const currentUrl = new URL(window.location.href);
  const externalReferrer = getExternalReferrer();

  return {
    capturedAt: new Date().toISOString(),
    landingPath: truncate(
      `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
      1024,
    ),
    referrerUrl: externalReferrer
      ? truncate(externalReferrer.toString(), 1024)
      : null,
    referrerHost: externalReferrer ? truncate(externalReferrer.host, 255) : null,
    utm: readStringParams<MarketingQueryParam>(
      currentUrl.searchParams,
      MARKETING_QUERY_PARAMS,
    ),
    clickIds: readStringParams<MarketingClickIdParam>(
      currentUrl.searchParams,
      MARKETING_CLICK_ID_PARAMS,
    ),
  };
}

function readStoredAttribution(): MarketingAttribution | null {
  if (typeof window === "undefined") return null;
  return parseMarketingAttribution(localStorage.getItem(ATTRIBUTION_STORAGE_KEY));
}

function persistAttribution(attribution: MarketingAttribution) {
  const serialized = stringifyMarketingAttribution(attribution);

  localStorage.setItem(ATTRIBUTION_STORAGE_KEY, serialized);

  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie =
    `${MARKETING_ATTRIBUTION_COOKIE_NAME}=${encodeURIComponent(serialized)}; ` +
    `Max-Age=${ATTRIBUTION_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

export function captureMarketingAttribution() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!hasMarketingConsent()) return;

  const existingAttribution = readStoredAttribution();
  const touchpoint = buildTouchpoint();
  const hasTrackedParams =
    Object.keys(touchpoint.utm).length > 0 ||
    Object.keys(touchpoint.clickIds).length > 0;

  if (!existingAttribution && !hasMarketingSignal(touchpoint)) {
    return;
  }

  if (existingAttribution && !hasTrackedParams) {
    persistAttribution(existingAttribution);
    return;
  }

  const attribution: MarketingAttribution = existingAttribution
    ? {
        ...existingAttribution,
        lastTouch: touchpoint,
      }
    : {
        version: 1,
        firstTouch: touchpoint,
        lastTouch: touchpoint,
      };

  persistAttribution(attribution);
}
